import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

export type Device = "mobile" | "tablet" | "desktop";
export const DEVICE_WIDTH: Record<Device, number | null> = { mobile: 390, tablet: 768, desktop: null };

/**
 * Device frame used by the editor canvas and the draft preview. Width drives container queries.
 * `flushOnPhone`: on phone-sized screens drop the bezel and rounding so the site uses the full width.
 * The screen clips with `overflow: clip`, not hidden: it creates no scroll container, so the site's
 * sticky navbar still sticks.
 */
export function DeviceFrame({ device, children, className, flushOnPhone }: { device: Device; children: ReactNode; className?: string; flushOnPhone?: boolean }) {
  const w = DEVICE_WIDTH[device];
  return (
    <div
      className={cn(
        "mx-auto bg-white transition-[width,max-width,border-radius] duration-300 ease-out",
        device === "mobile" && "rounded-[36px] p-2.5 shadow-[0_0_0_1px_rgb(15_23_42/0.12),0_30px_80px_-20px_rgb(15_23_42/0.35)] bg-slate-900",
        device === "tablet" && "rounded-[24px] p-2 shadow-[0_0_0_1px_rgb(15_23_42/0.1),0_30px_80px_-24px_rgb(15_23_42/0.3)] bg-slate-800",
        device === "desktop" && "rounded-2xl shadow-[0_0_0_1px_rgb(15_23_42/0.08),0_30px_80px_-30px_rgb(15_23_42/0.25)]",
        flushOnPhone && "max-sm:rounded-none max-sm:p-0 max-sm:shadow-none",
        className,
      )}
      style={{ width: w ? w + (device === "mobile" ? 20 : 16) : "100%", maxWidth: w ? "100%" : 1280 }}
    >
      <div className={cn("relative overflow-clip bg-white", device === "mobile" ? "rounded-[28px]" : device === "tablet" ? "rounded-[18px]" : "rounded-2xl", flushOnPhone && "max-sm:rounded-none")}>{children}</div>
    </div>
  );
}
